import { ChevronDown, X } from "lucide-react";

import type { PayoutStatus } from "@/modules/dashboard/dispersions/services/paymentsPayout.service";
import type { PayinRuntimeStatus } from "@/modules/dashboard/payins/services/paymentsPayin.service";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaTabs, type ColocaTabItem } from "@/shared/components/navigation/ColocaTabs";
import { ColocaDropdownMenu } from "@/shared/components/overlays/ColocaDropdownMenu";
import useTranslations from "@/shared/lang/useTranslations";

import {
  CORRIDOR_OPTIONS,
  DATE_RANGE_OPTIONS,
  STATUS_FILTER_OPTIONS,
  STATUS_LABEL_KEYS,
} from "../constants/paymentsActivity.constants";
import type { ActivityDirection, ActivityFiltersState } from "../types/paymentsActivity.types";

export interface ActivityFiltersProps {
  direction: ActivityDirection;
  onDirectionChange: (direction: ActivityDirection) => void;
  filters: ActivityFiltersState;
  onStatusChange: (status: PayinRuntimeStatus | PayoutStatus | null) => void;
  onCountryChange: (country: string | null) => void;
  onRangeDaysChange: (days: number | null) => void;
  onClear: () => void;
  hasActiveFilters: boolean;
}

/**
 * Toolbar: the Payins / Payouts segmented control (it switches the ENDPOINT and the status vocabulary — the
 * two enums are different) plus the only filters the API supports server-side: status (single enum value),
 * corridor (`country`) and a rolling created_at window (`from`). There is deliberately NO search box — the
 * runtime endpoints have no free-text search.
 */
export function ActivityFilters({
  direction,
  onDirectionChange,
  filters,
  onStatusChange,
  onCountryChange,
  onRangeDaysChange,
  onClear,
  hasActiveFilters,
}: ActivityFiltersProps) {
  const { t } = useTranslations("paymentsActivity");

  const directionTabs: ColocaTabItem[] = [
    { value: "payins", label: t("tab_payins"), content: null },
    { value: "payouts", label: t("tab_payouts"), content: null },
  ];

  // Same label map as the table pill (STATUS_LABEL_KEYS) so filtering by "Acreditado" surfaces rows whose
  // pill reads "Acreditado"; a status the map doesn't know yet falls back to the raw enum value.
  const statusLabel = (status: string) => {
    const key = STATUS_LABEL_KEYS[status];
    return key ? t(key) : status;
  };

  const statusItems = [
    {
      label: t("filter_all_statuses"),
      onClick: () => onStatusChange(null),
    },
    ...STATUS_FILTER_OPTIONS[direction].map((status) => ({
      label: statusLabel(status),
      onClick: () => onStatusChange(status as PayinRuntimeStatus | PayoutStatus),
    })),
  ];

  const corridorItems = [
    { label: t("filter_all_corridors"), onClick: () => onCountryChange(null) },
    ...CORRIDOR_OPTIONS.map((corridor) => ({
      label: `${corridor.country} · ${corridor.rail} · ${corridor.currency}`,
      onClick: () => onCountryChange(corridor.country),
    })),
  ];

  const rangeItems = [
    { label: t("filter_all_dates"), onClick: () => onRangeDaysChange(null) },
    ...DATE_RANGE_OPTIONS.map((days) => ({
      label: t("filter_last_days", { days }),
      onClick: () => onRangeDaysChange(days),
    })),
  ];

  const activeCorridor = CORRIDOR_OPTIONS.find((c) => c.country === filters.country);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-card p-3 shadow-sm">
      <ColocaTabs
        tabs={directionTabs}
        value={direction}
        onValueChange={(v) => onDirectionChange(v as ActivityDirection)}
      />

      <div className="flex flex-wrap items-center gap-2">
        <ColocaDropdownMenu
          trigger={
            <ColocaButton variant="default" size="sm" iconTrailing={ChevronDown}>
              {filters.status ? statusLabel(filters.status) : t("filter_status_label")}
            </ColocaButton>
          }
          items={statusItems}
          align="end"
        />
        <ColocaDropdownMenu
          trigger={
            <ColocaButton variant="default" size="sm" iconTrailing={ChevronDown}>
              {activeCorridor
                ? `${activeCorridor.country} · ${activeCorridor.rail}`
                : t("filter_corridor_label")}
            </ColocaButton>
          }
          items={corridorItems}
          align="end"
        />
        <ColocaDropdownMenu
          trigger={
            <ColocaButton variant="default" size="sm" iconTrailing={ChevronDown}>
              {filters.rangeDays
                ? t("filter_last_days", { days: filters.rangeDays })
                : t("filter_date_label")}
            </ColocaButton>
          }
          items={rangeItems}
          align="end"
        />
        {hasActiveFilters && (
          <ColocaButton variant="ghost" size="sm" icon={X} onClick={onClear}>
            {t("filter_clear")}
          </ColocaButton>
        )}
      </div>
    </div>
  );
}
